import React, { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import ImageCarousel from '../components/ImageCarousel';
import Lightbox from '../components/Lightbox';

const HistoryContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #edf2f7 25%, #e2e8f0 50%, #cbd5e0 75%, #f7fafc 100%);
  padding: 6rem 2rem 4rem 2rem;
  color: #1a365d;
  position: relative;

  @media (max-width: 768px) {
    padding: 4rem 1rem 2rem 1rem;
  }
`;

const BackgroundEffects = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(circle at 70% 30%, rgba(66, 153, 225, 0.06) 0%, transparent 50%);
  pointer-events: none;
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

const Title = styled.h1`
  text-align: center;
  font-size: clamp(2.5rem, 6vw, 3.5rem);
  font-weight: 300;
  margin-bottom: 3rem;
  background: linear-gradient(135deg, #1a365d, #3182ce, #4299e1);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: 2px;
  text-shadow: 0 0 30px rgba(66, 153, 225, 0.3);
`;

const Subtitle = styled.p`
  text-align: center;
  font-size: clamp(1rem, 3vw, 1.2rem);
  color: #718096;
  margin-bottom: 4rem;
  max-width: 800px;
  margin: 0 auto 4rem auto;
  line-height: 1.6;
  padding: 0 1rem;

  @media (max-width: 768px) {
    margin-bottom: 3rem;
  }
`;

const Timeline = styled.div`
  display: grid;
  gap: 3rem;
  position: relative;

  @media (max-width: 768px) {
    gap: 2rem;
  }
`;

const TimelineLine = styled.div`
  position: absolute;
  left: 50%;
  top: 0;
  bottom: 0;
  width: 2px;
  background: linear-gradient(180deg, #3182ce, #4299e1, #63b3ed);
  transform: translateX(-50%);
  z-index: -1;

  @media (max-width: 1024px) {
    left: 2rem;
    transform: none;
  }

  @media (max-width: 768px) {
    left: 1rem;
  }
`;

const PhaseContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  align-items: center;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 2rem;
    padding-left: 4rem;
  }

  @media (max-width: 768px) {
    padding-left: 2rem;
    gap: 1.5rem;
  }
`;

const PhaseCard = styled.div`
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(66, 153, 225, 0.08));
  padding: 2.5rem;
  border-radius: 20px;
  border: 1px solid rgba(66, 153, 225, 0.2);
  box-shadow: 0 8px 32px rgba(66, 153, 225, 0.1);
  position: relative;
  transition: all 0.3s ease;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, transparent, #3182ce, transparent);
    border-radius: 20px 20px 0 0;
  }

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 40px rgba(66, 153, 225, 0.15);
  }

  @media (max-width: 1024px) {
    padding: 2rem;
  }

  @media (max-width: 768px) {
    padding: 1.5rem;
    border-radius: 15px;
  }
`;

const PhaseTitle = styled.h3`
  font-size: clamp(1.3rem, 4vw, 1.8rem);
  color: #3182ce;
  margin-bottom: 1rem;
  font-weight: 300;

  @media (max-width: 768px) {
    margin-bottom: 0.75rem;
  }
`;

const PhaseDescription = styled.p`
  color: #4a5568;
  line-height: 1.7;
  margin-bottom: 1.5rem;
  font-size: clamp(0.9rem, 2vw, 1rem);

  @media (max-width: 768px) {
    margin-bottom: 1rem;
    line-height: 1.6;
  }
`;

const TagsContainer = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 0.5rem;
  }
`;

const Tag = styled.span`
  background: rgba(66, 153, 225, 0.1);
  color: #3182ce;
  padding: 0.5rem 1rem;
  border-radius: 15px;
  font-size: 0.85rem;
  font-weight: 500;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(66, 153, 225, 0.15);
    transform: translateY(-1px);
  }

  @media (max-width: 768px) {
    padding: 0.4rem 0.8rem;
    font-size: 0.8rem;
    border-radius: 12px;
  }
`;



const SpecsSection = styled.div`
  margin-top: 5rem;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(66, 153, 225, 0.05));
  padding: 3rem;
  border-radius: 20px;
  border: 1px solid rgba(66, 153, 225, 0.2);
  box-shadow: 0 8px 32px rgba(66, 153, 225, 0.1);

  @media (max-width: 1024px) {
    margin-top: 4rem;
    padding: 2.5rem;
  }

  @media (max-width: 768px) {
    margin-top: 3rem;
    padding: 2rem;
    border-radius: 15px;
  }

  @media (max-width: 480px) {
    padding: 1.5rem;
  }
`;

const SpecsTitle = styled.h3`
  text-align: center;
  font-size: clamp(1.5rem, 4vw, 2rem);
  color: #3182ce;
  margin-bottom: 2rem;
  font-weight: 300;

  @media (max-width: 768px) {
    margin-bottom: 1.5rem;
  }
`;

const SpecsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 1.5rem;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const SpecCard = styled.div`
  text-align: center;
  padding: 1.5rem;
  background: rgba(66, 153, 225, 0.08);
  border-radius: 15px;
  border: 1px solid rgba(66, 153, 225, 0.2);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-3px);
    background: rgba(66, 153, 225, 0.12);
    box-shadow: 0 8px 20px rgba(66, 153, 225, 0.15);
  }

  @media (max-width: 768px) {
    padding: 1.25rem;
    border-radius: 12px;
  }

  @media (max-width: 480px) {
    padding: 1rem;
  }
`;

const SpecLabel = styled.div`
  font-size: clamp(0.8rem, 2vw, 0.9rem);
  color: #3182ce;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 0.5rem;
  font-weight: 500;

  @media (max-width: 768px) {
    margin-bottom: 0.4rem;
  }
`;

const SpecValue = styled.div`
  font-size: clamp(1rem, 3vw, 1.2rem);
  color: #1a365d;
  font-weight: 300;
`;

const History: React.FC = () => {
  const { t } = useTranslation();
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState({ src: '', alt: '' });

  const openLightbox = (src: string, alt: string) => {
    setLightboxImage({ src, alt });
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  // Images for each phase
  const phaseImages = {
    1: [
      { src: '/images/amel-55-layout-1.webp', alt: 'Amel 55 Layout Design' },
      { src: '/images/2015-Amel-55-WIRRUNA-for-sale-003-1024x327.jpg', alt: 'Amel 55 Design Blueprint' }
    ],
    2: [
      { src: '/images/photo_2025-07-18_14-33-14.jpg', alt: 'Hull Construction Process' },
      { src: '/images/photo_2025-07-18_14-33-14 (2).jpg', alt: 'Hull Molding Phase' }
    ],
    3: [
      { src: '/images/photo_2025-07-18_14-39-27.jpg', alt: 'Systems Integration' },
      { src: '/images/photo_2025-07-18_14-39-26.jpg', alt: 'Electronic Systems Installation' }
    ],
    4: [
      { src: '/images/photo_2025-07-18_14-34-58.jpg', alt: 'Launch Day' },
      { src: '/images/photo_2025-07-18_14-34-57.jpg', alt: 'Sea Trials' },
      { src: '/images/photo_2025-07-18_14-34-58 (2).jpg', alt: 'First Sail' }
    ]
  };

  const phases = [
    {
      title: 'Phase 1: Design & Planning',
      description: 'Extensive design phase working with Amel\'s experienced naval architects. Focus on ocean-going capability, comfort, and reliability for long-distance cruising.',
      tags: ['Hull Design', 'Sail Plan', 'Systems Layout'],
      icon: '📐',
      placeholderText: 'Design Plans & Blueprints'
    },
    {
      title: 'Phase 2: Hull Construction',
      description: 'Premium fiberglass construction using advanced composites and vacuum-infusion techniques. Built to withstand the harshest ocean conditions.',
      tags: ['Fiberglass Hull', 'Keel Installation', 'Deck Molding'],
      icon: '🔨',
      placeholderText: 'Hull Construction'
    },
    {
      title: 'Phase 3: Systems Integration',
      description: 'Installation of advanced navigation, communication, and safety systems. Solar panels, wind generator, and water makers for self-sufficiency.',
      tags: ['Electronics', 'Solar Power', 'Water Systems'],
      icon: '⚡',
      placeholderText: 'Systems Installation'
    },
    {
      title: 'Phase 4: Launch & Sea Trials',
      description: 'The moment of truth - launch day and comprehensive sea trials. Testing all systems and fine-tuning for the upcoming world adventure.',
      tags: ['First Launch', 'Sea Trials', 'Final Preparations'],
      icon: '🌊',
      placeholderText: 'Launch Day'
    }
  ];

  const specifications = [
    { label: 'Length Overall', value: '15.5m (51ft)' },
    { label: 'Beam', value: '4.9m (16ft)' },
    { label: 'Draft', value: '1.8m (6ft)' },
    { label: 'Displacement', value: '18,000kg' },
    { label: 'Sail Area', value: '120m²' },
    { label: 'Engine', value: '80HP Diesel' }
  ];

  return (
    <HistoryContainer>
      <BackgroundEffects />
      
      <ContentWrapper>
        <Title>{t('history.title')}</Title>
        <Subtitle>
          {t('history.subtitle')}
        </Subtitle>

        <Timeline>
          <TimelineLine />
          
          {phases.map((phase, index) => (
            <PhaseContainer key={index}>
              {index % 2 === 0 ? (
                <>
                  <PhaseCard>
                    <PhaseTitle>{phase.title}</PhaseTitle>
                    <PhaseDescription>{phase.description}</PhaseDescription>
                    <TagsContainer>
                      {phase.tags.map((tag, tagIndex) => (
                        <Tag key={tagIndex}>{tag}</Tag>
                      ))}
                    </TagsContainer>
                  </PhaseCard>
                  
                  <ImageCarousel 
                    images={phaseImages[index + 1 as keyof typeof phaseImages] || []}
                    onImageClick={openLightbox}
                  />
                </>
              ) : (
                <>
                  <ImageCarousel 
                    images={phaseImages[index + 1 as keyof typeof phaseImages] || []}
                    onImageClick={openLightbox}
                  />
                  
                  <PhaseCard>
                    <PhaseTitle>{phase.title}</PhaseTitle>
                    <PhaseDescription>{phase.description}</PhaseDescription>
                    <TagsContainer>
                      {phase.tags.map((tag, tagIndex) => (
                        <Tag key={tagIndex}>{tag}</Tag>
                      ))}
                    </TagsContainer>
                  </PhaseCard>
                </>
              )}
            </PhaseContainer>
          ))}
        </Timeline>

        <SpecsSection>
          <SpecsTitle>{t('history.final_specifications')}</SpecsTitle>
          <SpecsGrid>
            {specifications.map((spec, index) => (
              <SpecCard key={index}>
                <SpecLabel>{spec.label}</SpecLabel>
                <SpecValue>{spec.value}</SpecValue>
              </SpecCard>
            ))}
          </SpecsGrid>
        </SpecsSection>
      </ContentWrapper>

      <Lightbox
        isOpen={lightboxOpen}
        imageSrc={lightboxImage.src}
        imageAlt={lightboxImage.alt}
        onClose={closeLightbox}
      />
    </HistoryContainer>
  );
};

export default History; 